// src/contexts/DataContext.jsx

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

const DataContext = createContext(null);

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const fetchEvents = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('get-ai-events', { method: 'GET' });
    if (error) throw error;
    return data || [];
  }, []);

  const fetchVideos = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('get-video-insights', { method: 'GET' });
    if (error) throw error;
    return data || [];
  }, []);

  const fetchPulse = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('get-ai-pulse', { method: 'GET' });
    if (error) throw error;
    return data || [];
  }, []);

  const fetchExpertTakes = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke('get-featured-expert-takes', { method: 'GET' });
    if (error) throw error;
    return data || [];
  }, []);

  const results = useQueries({
    queries: [
      { queryKey: ['events'], queryFn: fetchEvents, staleTime: 15 * 60 * 1000 },
      { queryKey: ['videos'], queryFn: fetchVideos, staleTime: 15 * 60 * 1000 },
      { queryKey: ['pulse'], queryFn: fetchPulse, staleTime: 15 * 60 * 1000 },
      { queryKey: ['expertTakes'], queryFn: fetchExpertTakes, staleTime: 15 * 60 * 1000 },
    ]
  });

  const [eventsResult, videosResult, pulseResult, expertTakesResult] = results;

  // --- SIMPLIFIED: All manual useEffects for focus/visibility/inactivity have been removed ---
  // React Query's global `refetchOnWindowFocus` setting in App.jsx now handles this automatically and efficiently.

  const value = useMemo(() => ({
    events: eventsResult.data || [],
    videos: videosResult.data || [],
    pulse: pulseResult.data || [],
    expertTakes: expertTakesResult.data || [],
    loading: results.some(result => result.isLoading),
    error: results.find(result => result.error)?.error?.message || null,
    refetch: (options) => queryClient.invalidateQueries(options?.queryKey),
  }), [results, queryClient]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};